/**
 * Generic Workflow Settings
 */

import { clone, isArray } from 'lodash'
import Vue from 'vue'

import { DEFAULT_WORKFLOW_PERMISSIONS } from '@/store/shared/partials/workflow'
import { SETTINGS_DIALOG_MODE } from '@/store/settings/util'
import { axiosRequest } from '@/plugins/axios-common'
import { handleError } from '@/store/handle-error'

export const WORKFLOW_SETTINGS_EMPTY = {
  comment: '',
  docket_entry_type_to_create_id: null,
  in_days: null,
  all_teams: false,
  assignees: [],
  use_default_comment: true,
}

export const WorkflowSettingsState = {
  workflowSettingsList: [],
  workflowSettingsPermissions: {
    editors: [],
    skippers: [],
  },
  workflowSettingsShowDialog: false,
  workflowSettingsEditMode: SETTINGS_DIALOG_MODE.detail,
  workflowSettingsConfigured: false,
}

const workflowListSorting = (reverse = false) => (a, b) => {
  const aDays = a.daysPosition === 'before' ? -a.days : a.days
  const bDays = b.daysPosition === 'before' ? -b.days : b.days
  const compResult = reverse ? -1 : 1

  if (aDays === bDays) return 0
  return aDays > bDays ? compResult : -compResult
}

export const WorkflowSettingsActions = {
  setupWorkflowSettings: async ({ commit, getters }, workflows) => {
    if (isArray(workflows) && workflows.length > 0) {
      const genericModel = getters.genericModelSelectedModel

      // eslint-disable-next-line camelcase
      const processedWorkflows = workflows.map(({ in_days, ...w }) =>
        ({
          ...w,
          days: Math.abs(in_days),
          daysPosition: Math.sign(in_days) === -1 ? 'before' : 'after',
        }))

      commit('setWorkflowSettingsList', processedWorkflows)
      commit('setWorkflowSettingsConfigured', true)
      commit('setWorkflowSettingsPermissions', genericModel.workflow_permission || clone(DEFAULT_WORKFLOW_PERMISSIONS))
    } else {
      commit('setWorkflowSettingsList', [clone(WORKFLOW_SETTINGS_EMPTY)])
      commit('setWorkflowSettingsConfigured', false)
      commit('setWorkflowSettingsPermissions', clone(DEFAULT_WORKFLOW_PERMISSIONS))
    }
  },
  updateWorkflowSettings: ({ commit }, workflows) => {
    commit('setWorkflowSettingsList', workflows)
  },
  addWorkflowSettingsItem: ({ state, commit }) => {
    const nextList = [...state.workflowSettingsList, clone(WORKFLOW_SETTINGS_EMPTY)]
    commit('setWorkflowSettingsList', nextList)
  },
  sortWorkflowSettingsList: ({ state, commit }, reversed) => {
    const orderedList = state.workflowSettingsList.sort(workflowListSorting(reversed))
    commit('setWorkflowSettingsList', orderedList)
  },
  workflowSettingsFetchDocketEntry: async ({ commit }, payload) => {
    return axiosRequest.get(`/docket_entry_types/${payload}`).
      then(({ data }) => data?.docket_entry_type).
      catch(handleError)
  },
}

export const WorkflowSettingsMutations = {
  setWorkflowSettingsDialogShow: (state, payload) => { state.workflowSettingsShowDialog = payload },
  setWorkflowSettingsList: (state, payload) => { Vue.set(state, 'workflowSettingsList', payload) },
  setWorkflowSettingsConfigured: (state, payload) => { state.workflowSettingsConfigured = payload },
  setWorkflowSettingsPermissions: (state, payload) => { Vue.set(state, 'workflowSettingsPermissions', payload) },
}

export const WorkflowSettingsGetters = {
  isWorkflowSettingsDialogShow: state => state.workflowSettingsShowDialog,
  currentWorkflowSettingsList: state => state.workflowSettingsList,
  currentWorkflowSettingsConfigured: state => state.workflowSettingsConfigured,
  currentWorkflowSettingsListProcessed: state => state.workflowSettingsList?.map(({ days, daysPosition, ...w }) => ({
    ...w,
    in_days: daysPosition === 'before' ? -days : +days,
  })),
  currentWorkflowSettingsPermissions: state => state.workflowSettingsPermissions,
}
